import { useCallback, useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Trash2, Pencil, ListChecks } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["text", "number", "date", "select", "boolean"];
const EMPTY = { key: "", label: "", type: "text", required: false, options: "" };
const toKey = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

export default function FieldDefinitionsPage() {
  const [defs, setDefs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    const { data } = await api.get("/field-definitions");
    setDefs(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (d) => {
    setEditing(d);
    setForm({ key: d.key, label: d.label, type: d.type,
              required: !!d.required, options: (d.options || []).join(", ") });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      key: form.key || toKey(form.label),
      label: form.label, type: form.type, required: form.required,
      options: form.type === "select"
        ? form.options.split(",").map((s) => s.trim()).filter(Boolean)
        : null,
    };
    try {
      if (editing) {
        await api.patch(`/field-definitions/${editing.id}`, payload);
        toast.success("Field updated");
      } else {
        await api.post("/field-definitions", payload);
        toast.success("Field created");
      }
      setOpen(false); setForm(EMPTY); setEditing(null); load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete custom field '${d.label}'? Existing values on user profiles are kept but the field disappears from forms.`)) return;
    try {
      await api.delete(`/field-definitions/${d.id}`);
      toast.success("Field deleted"); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3"><ListChecks className="text-primary" /><h1 className="heading text-3xl md:text-4xl">Custom fields</h1></div>
        <Button onClick={startNew} data-testid="new-field-btn"><Plus size={14} className="mr-2" />New field</Button>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Extra attributes you'd like to capture on every user profile — Emergency Contact,
        Referred By, GST number, whatever your operations need. Values live on each user's
        <code className="mx-1 px-1.5 py-0.5 rounded bg-secondary/60 text-xs">custom</code>
        map and appear on the Users edit modal.
      </p>

      <div className="border border-border rounded-lg bg-card divide-y divide-border">
        {defs.length === 0 && <div className="p-6 text-center text-xs text-muted-foreground" data-testid="empty-defs">No custom fields yet. Add one to start capturing extra info on user profiles.</div>}
        {defs.map((d) => (
          <div key={d.id} className="p-4 flex items-center gap-3" data-testid={`def-${d.key}`}>
            <div className="flex-1">
              <div className="text-sm font-medium">{d.label}</div>
              <div className="text-xs text-muted-foreground">
                <code className="px-1 rounded bg-secondary/50">{d.key}</code> · {d.type}{d.required ? " · required" : ""}{d.options ? ` · ${d.options.join(", ")}` : ""}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => startEdit(d)} data-testid={`edit-${d.key}`}><Pencil size={13} /></Button>
            <Button variant="ghost" size="sm" onClick={() => remove(d)} data-testid={`delete-${d.key}`}><Trash2 size={13} /></Button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="field-modal">
          <DialogHeader><DialogTitle>{editing ? "Edit field" : "New custom field"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><Label>Label</Label><Input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value, key: editing ? form.key : toKey(e.target.value) })} data-testid="field-label-input" /></div>
            <div>
              <Label>Key <span className="text-muted-foreground">(machine name)</span></Label>
              <Input required disabled={!!editing} value={form.key} onChange={(e) => setForm({ ...form, key: toKey(e.target.value) })} data-testid="field-key-input" />
              <div className="text-[11px] text-muted-foreground mt-1">Snake_case, unique. Auto-derived from label; edit only when creating.</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="field-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <label className="flex items-end gap-2 pb-1 text-sm">
                <input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} data-testid="field-required-toggle" />
                Required
              </label>
            </div>
            {form.type === "select" && (
              <div>
                <Label>Options <span className="text-muted-foreground">(comma-separated)</span></Label>
                <Input value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} placeholder="e.g. Bronze, Silver, Gold" data-testid="field-options-input" />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={!form.label} data-testid="field-save-btn">{editing ? "Save" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
